import type React from "react";
import {
	type FormEvent,
	type MouseEvent,
	useContext,
	useId,
	useState,
} from "react";
import { Link, useNavigate } from "react-router";
import { Alert } from "~/components/Alert";
import {
	ContentContainer,
	ContentContainerWrapper,
} from "~/components/ContentContainer";
import { AccountIcon } from "~/components/icons";
import { DefaultLayout } from "~/components/Layout";
import { ModalAction, ModalContent, useModal } from "~/components/Modal";
import { useSnackBar } from "~/components/SnackBar";
import { SessionContext } from "~/ory";
import { createFlowErrorHandler } from "~/utils/errorHandler";

export const DeleteAccount: React.FC = () => {
	const id = useId();
	const [openModal, closeModal] = useModal();
	const [openSnackBar] = useSnackBar();
	const session = useContext(SessionContext);
	const [email, setEmail] = useState<string>("");
	const [emailError, setEmailError] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const navigate = useNavigate();

	if (!session) return <p>Unauthenticated</p>;

	const onSubmit = async (event: FormEvent<HTMLFormElement> | MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();

		if (email !== session.account.identity.email) {
			setEmailError(true);
			return;
		}

		if (isSubmitting) return;
		setIsSubmitting(true);

		try {
			const res = await fetch("/api/v2/account", {
				method: "DELETE",
				credentials: "same-origin",
				cache: "no-cache",
			});

			if (res.status !== 200) {
				const error = await res.json();
				openModal({
					type: "error",
					title: "FAILED TO DELETE YOUR ACCOUNT",
					children: (
						<>
							<ModalContent>
								<p className="text-center text-error">
									<h2 className="font-normal text-xl font-semibold mb-4">
										ERRORS
									</h2>
									<span>
										{error.error?.message ?? "Unexpected error occurred"}
									</span>
								</p>
							</ModalContent>
							<ModalAction>
								<button
									type="button"
									onClick={closeModal}
									className="btn btn-lg btn-t4-border rounded-full"
								>
									CLOSE
								</button>
							</ModalAction>
						</>
					),
				});
				return;
			}
			navigate("/account/complete-delete");
		} catch (err) {
			createFlowErrorHandler(openSnackBar, openModal, closeModal)(err);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<title>Delete Account | TIER IV Account</title>
			<DefaultLayout>
				<ContentContainerWrapper>
					<ContentContainer size="lg">
						<h1 className="mt-10 text-center font-normal">
							DELETE YOUR ACCOUNT
						</h1>
						<Alert type="warning" withIcon>
							Once your account is deleted, your account is no longer available
							in our system. You won’t be able to reactivate your previous
							account.
						</Alert>
						<form onSubmit={onSubmit} className="form-control mt-8">
							<h2 className="font-normal text-xl">EMAIL VERIFICATION</h2>
							<div className="form-control mt-4">
								<fieldset
									className={`t4-form-fieldset ${emailError && "error"}`}
								>
									<legend className="t4-input-label text-xs">EMAIL</legend>
									<div className="flex justify-center items-center px-3">
										<AccountIcon
											color={{
												dark: "#C6C6C5",
												light: "#C6C6C5",
											}}
										/>
										<input
											className="input w-full"
											onChange={(e) => {
												setEmailError(false);
												setEmail(e.target.value);
											}}
											id={`${id}-email`}
											type="email"
											name="email"
											placeholder="Enter email address"
											value={email}
										/>
									</div>
								</fieldset>
								<label className="label" htmlFor={undefined}>
									<span
										className={`t4-label-text-alt ${emailError && "error-text"}`}
									>
										Please enter your email address for verification.
									</span>
								</label>
							</div>
							<div className="flex flex-col items-center md:flex-row md:justify-center md:gap-3 mt-8">
								<Link
									className="btn btn-t4-border rounded-full w-full md:w-4/12 "
									to="/"
								>
									CLOSE
								</Link>
								<button
									type="submit"
									className="btn btn-t4-danger rounded-full w-full md:w-4/12 "
									name="submit"
									value=""
									disabled={isSubmitting}
								>
									DELETE ACCOUNT
								</button>
							</div>
						</form>
					</ContentContainer>
				</ContentContainerWrapper>
			</DefaultLayout>
		</>
	);
};
